import { resolve } from 'path';
import { Injectable, Inject, InjectionToken } from '@angular/core';
import { HttpInterceptor, HttpClient, HttpHeaders, HttpRequest, HttpEvent, HttpHandler } from '@angular/common/http';
import { Subscription, Observable } from 'rxjs';
import { Dict, Decorable } from '../../types/decorable';
import { CookieService } from 'ngx-cookie-service';
import { EnvService } from '../../env.service';
import { environment } from 'src/environments/environment';
import { CancellablePromise } from 'src/app/types/cancellable-promise';
import { timeout } from 'rxjs/operators';
import { AnyHandler } from 'src/app/types/@types/sl-v-types';
import { UUID } from 'angular2-uuid';

export const DEFAULT_TIMEOUT = new InjectionToken<number>('defaultTimeout');
@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  constructor(@Inject(DEFAULT_TIMEOUT) protected defaultTimeout: number) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const timeoutValue = req.headers.get('timeout') || this.defaultTimeout;
    const timeoutValueNumeric = Number(timeoutValue);
    return next.handle(req).pipe(timeout(timeoutValueNumeric));
  }
}
@Injectable({
  providedIn: 'root'
})
export class SlWapiService {
  static SLSESSION: Dict = {};

  private baseURL: string;
  private appName: string;
  private session: Dict;
  private unauthSession: Dict;
  private authTo: string;
  private tabsReady = true;

  private static SetSLSession(session: Dict) {
    SlWapiService.SLSESSION = session;
  }
  constructor(private http: HttpClient, private cookies: CookieService, private env: EnvService) {
    this.appName = 'SimpleLayers V Apps';
    this.baseURL = environment.wapiURL;
    this.authTo = '/';
    this.tabsReady = this.tabsReady;
  }
  Post(wapiURL: string, payLoad: Decorable, asJSON: boolean = true): CancellablePromise {
    payLoad.token = this.GetToken();
    if (!payLoad.hasOwnProperty('application')) {
      payLoad['application'] = this.appName;
    }

    if (asJSON) payLoad.format = 'json';

    const postData = new FormData();
    for (const key of Object.keys(payLoad)) {
      postData.append(key, payLoad[key]);
    }
    let subscription: Subscription | null = null;
    let aborted = false;
    let done = false;

    let thenHandler: ((val: any) => void) | null = null;
    let catchHandler: ((err: any) => void) | null = null;
    let finallyHandler: (() => void) | null = null;

    // Buffer for late handler attachment
    let resultData: any = undefined;
    let errorData: any = undefined;

    const controller: CancellablePromise = {
      aborted: false,
      id: UUID.UUID(),

      then(onFulfilled, onRejected) {
        thenHandler = onFulfilled;
        if (onRejected) catchHandler = onRejected;

        // If already completed, call immediately
        if (done) {
          if (resultData !== undefined) onFulfilled?.(resultData);
          if (errorData !== undefined) onRejected?.(errorData);
        }
        return controller;
      },

      catch(onRejected) {
        catchHandler = onRejected;
        if (done && errorData !== undefined) onRejected?.(errorData);
        return controller;
      },

      finally(onFinally) {
        finallyHandler = onFinally;
        if (done) onFinally?.();
        return controller;
      },

      abort() {
        if (aborted || done) return;
        aborted = true;
        done = true;

        subscription?.unsubscribe();
        errorData = { status: 'aborted' };
        catchHandler?.(errorData);
        finallyHandler?.();

        cleanup();
        controller.aborted = true;

        // Optionally disable methods after abort
        controller.then = undefined!;
        controller.catch = undefined!;
        controller.finally = undefined!;
        controller.abort = undefined!;
      }
    };

    function cleanup() {
      subscription = null;
      thenHandler = null;
      catchHandler = null;
      finallyHandler = null;
      postData.forEach((_, key) => postData.delete(key));
    }

    subscription = this.http.post(this.baseURL + wapiURL, postData).subscribe({
      next(data: any) {
        if (aborted || done) return;
        done = true;

        if (!data) {
          errorData = { status: 'no result from server' };
          catchHandler?.(errorData);
          finallyHandler?.();
          cleanup();
          return;
        }

        if (data.hasOwnProperty('json')) {
          data = data.json;
        }

        if (data.status && data.status !== 'ok') {
          if (data.status === 'error' && !!data.problem) {
            resultData = data;
            thenHandler?.(data);
          } else {
            errorData = data.status;
            catchHandler?.(data.status);
          }
          finallyHandler?.();
          cleanup();
          return;
        }

        resultData = data;
        thenHandler?.(data);
        finallyHandler?.();
        cleanup();
      },

      error(err) {
        if (aborted || done) return;
        done = true;

        errorData = err;
        catchHandler?.(err);
        finallyHandler?.();
        cleanup();
      }
    });

    return controller;
  }





  SetAppName(appName: string): void {
    this.appName = appName;
  }
  Authenticate(uName?: string, pw?: string): Promise<any> {
    if (!!uName && !!pw) {
      return this.GetAuthPromise({ userName: uName, password: pw });
    } else {
      return this.GetAuthPromise({});
    }
  }
  AuthenticateAsSandboxed(): Promise<any> {
    return this.GetAuthPromise({ 'sandboxed': true });
  }
  AuthenticateAsVisitor(): Promise<any> {
    return this.GetAuthPromise({});
  }
  AuthenticatePublicToken(): Promise<any> {
    const params: any = { isPublic: true };
    params.token = this.GetToken(true);
    if (params.token === null) {
      delete params.token;
    }
    return this.GetAuthPromise(params);
  }
  AuthenticateToken(token?: string): Promise<any> {
    return this.GetAuthPromise({ token: !token ? this.GetToken() : token, isPublic: false });
  }
  _authPromise: Promise<any>;
  GetAuthPromise(params: Dict): Promise<any> {
    if (this._authPromise) return this._authPromise;
    this._authPromise = new Promise((resolve, reject) => {
      const asVisitor: boolean = (!!params.isPublic) ? params.isPublic : false;
      const sandboxed: boolean = (!!params.sandboxed) ? params.sandboxed : false;

      if (asVisitor === false) {
        if (params.token === null) {
          reject('no token');
          return;
        }
      }
      if (this.appName === '') { return false; }
      const body: URLSearchParams = new URLSearchParams();
      if (params.hasOwnProperty('userName')) {
        body.set('username', params.userName);
      }
      if (params.hasOwnProperty('password')) {
        body.set('password', params.password);
      }
      if (params.hasOwnProperty('token')) {
        body.set('token', params.token);
      }
      if (sandboxed) {
        body.set('sandboxed', 'true');
      }

      body.set('application', this.appName);
      const options = {
        headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
      };
      this.http.post(
        this.baseURL + 'auth/authenticate/', body.toString(), options
      ).subscribe(
        (data: any) => {
          if (data.hasOwnProperty('json')) {
            data = data.json;
          }
          if (data.hasOwnProperty('status')) {
            if (data.status !== 'ok') {
              reject(data);
              return;
            }
          }
          if (data.hasOwnProperty('token')) {
            const expires = new Date();
            expires.setDate(expires.getDate() + 1);
            const secure = !this.env.GetEnvParam('local');

            if (sandboxed) {
              data.isAuthorized = false;
              this.unauthSession = data;
              SlWapiService.SetSLSession(this.unauthSession);
            } else if (asVisitor) {
              data.isAuthorized = false;
              this.unauthSession = data;
              SlWapiService.SetSLSession(this.unauthSession);
              this.cookies.set('apps-session-unauth', data.token, null, '/public/', null, secure, 'None');
            } else {
              data.isAuthorized = (data.state === 'auth_ok');
              this.session = data;
              SlWapiService.SetSLSession(this.session);
              this.cookies.set('apps-session', data.token, null, null, null, secure, 'None');
            }
          }
          resolve(data);
        },
        error => reject(error)
      );
    });
    return this._authPromise;
  }

  Logout(): Promise<Decorable> {
    return new Promise((resolve, reject) => {
      if (this.appName === '') { return false; }
      const body: URLSearchParams = new URLSearchParams();

      body.set('token', this.GetToken());
      body.set('application', this.appName);
      body.set('format', 'json');
      const options = {
        headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
      };

      this.UnsetToken();
      this.http.post(
        this.baseURL + 'auth/deauthenticate/', body.toString(), options
      ).subscribe(
        (data: any) => {

          resolve(data);
        },
        error => reject(error)
      );
    });


  }
  SetAuthTo(where: string): void {
    this.authTo = decodeURI(where);
  }
  GetAuthTo(): string {
    return this.authTo;
  }
  HasAppsSession(): boolean {
    const appSession = this.cookies.get('apps-session');
    if (appSession === '') {
      return false;
    }
    return true;
  }
  GetDMICookie(): string {
    return this.cookies.get('SLSESSID');
  }
  GetAppCookie(): string {
    return this.cookies.get('apps-session');

  }
  HasDMICookie(): boolean {
    const cookieCheck = this.cookies.get('SLSESSID');
    if (this.env.GetAppURL().indexOf('localhost')) {
      return this.cookies.check('apps-session') === true;
    }
    if (this.env.GetAppURL().indexOf('ng-dev.app')) {
      return this.cookies.check('apps-session') === true;
    }

    if (cookieCheck === '') {
      return false;
    }
    console.log('Has DMI Session');
    return true;
  }
  HasAuthCookie(): boolean {
    if (this.HasDMICookie()) return true;
    if (this.HasAppsSession()) return true;
    return false;
  }
  HasSession(): boolean {
    return this.session !== undefined;
  }
  HasUnAuthCookie(): boolean {
    const unauth = this.cookies.get('apps-session-unauth');
    return !(unauth == '');
  }
  HasUnAuthSession(): boolean {
    return this.unauthSession !== undefined;
  }
  GetSession(): Dict | null {
    if (this.env.IsPublic()) {
      if (this.HasUnAuthSession()) {
        return this.unauthSession;
      } else {
        return null;
      }
    }
    if (this.HasSession()) {
      return this.session;
    }
    return null;
  }
  GetToken(isPublic?: boolean): string | null {
    if (this.env.IsSandboxed()) {
      if (!SlWapiService.SLSESSION) return null;
      return SlWapiService.SLSESSION?.token ? SlWapiService.SLSESSION.token : null;
    }
    if (isPublic === undefined) {
      isPublic = document.location.href.indexOf(environment.baseHref + 'public/') > -1;
    }
    if (isPublic) {
      if (this.HasUnAuthCookie()) {
        return this.cookies.get('apps-session-unauth');
      }
      return null;
    } else {

      if (this.HasDMICookie()) {
        if (this.HasAppsSession() === false) {
          return this.cookies.get('SLSESSID');
        }
      }
      if (this.HasAuthCookie()) {
        return this.cookies.get('apps-session');
      } else {

      }
      return null;
    }
  }
  IsAuthenticated(): boolean {
    if (this.env.IsPublic()) {
      return this.HasUnAuthSession() && this.HasUnAuthCookie();
    }
    return this.HasSession() && this.HasAuthCookie();

  }
  UnsetToken(): string | null {
    if (this.env.IsPublic()) {
      this.cookies.delete('apps-session-unauth');
      return null;
    } else {
      this.cookies.delete('apps-session');
      return null;
    }
  }
  IsPublicUser(): boolean | null {
    if (!this.IsAuthenticated()) {
      return false;
    }
    return (this.unauthSession.username === 'visitor@simplelayers');
  }
  DownloadFile(wapiURL: string, payLoad: Decorable, mimeType: string, fileName: string, responseHandler?: AnyHandler) {
    const postData = new FormData();
    for (const key of Object.keys(payLoad)) {
      postData.append(key, payLoad[key]);
    }
    postData.append('token', this.GetToken());

    this.http.post(this.baseURL + wapiURL,
      postData,
      {
        responseType: 'blob',
      }
    ).subscribe((response) => {
      const blob = new Blob([response], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      if (!!responseHandler) {
        responseHandler({ url: (url), fileName: (fileName) });

      } else {
        const a = $(
          '<a title="fileName" href="'
          + url + '" download="'
          + fileName
          + '">download</a>'
        );
        a.appendTo(window.document.body);
        a[0].click();
        a.remove();
      }

    });
  }
  GetWapiURL(relPath: string, includeToken: boolean = false): string {
    let url = this.baseURL;
    url += relPath;
    if (includeToken === true) {
      url += '/token:' + this.GetToken() + '/';
    }
    return url;
  }

}
